/// <amd-module name="TFS/Admin/Views/OrgGroups/Components/GroupsPageContentView" />
import * as React from "react";
import { connect } from "react-redux";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { IEntityType } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphMember, IGroupsViewState, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as ViewSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { MembershipAddPanelProps } from "TFS/Admin/Groups/Dialogs/MembershipAddPanel/MembershipAddPanel.Props";
import * as GroupSelectors from "TFS/Admin/Groups/Pivots/Groups/Redux/GroupsPivotSelectors";
import { IObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeFormat } from "VSS/Core/Util/String";
import { ContributedTabProvider } from "VSS/Features/Frame/ContributedTabProvider";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { GraphServicePrincipal, GraphUser } from "VSS/Platform/Contracts/Graph";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IButtonProps } from "VSSUI/Button";
import { FilterBar } from "VSSUI/FilterBar";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { IHubViewState } from "VSSUI/Utilities/HubViewState";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

interface IOrganizationGroupsContainerProps {
    hubViewState: IHubViewState;
    context: IVssComponentContext;
}

interface IGroupsPageContentViewOwnProps extends IOrganizationGroupsContainerProps {
    selectedGroup: PermissionsGroup | undefined;
    selectedUser: GraphUser | undefined;
    selectedServicePrincipal: GraphServicePrincipal | undefined;
    selectedMember: CustomGraphMember | undefined;
    selectedNavType: Constants.GroupsNavType;
    isAddGroupDisabled: boolean;
    updatedAvatarUrl?: string;
    filterPlaceholder: string | undefined;
    toMarkPermissionsTTI: boolean;
    isURLRedirect: boolean;
    showAddMemberPanel: boolean;
    pcaGroup: PermissionsGroup;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
}

export type IGroupsPageContentViewProps = IGroupsPageContentViewOwnProps & typeof ActionsToDispatch;

interface GroupsPageContentViewState {
    selectedTab: ObservableValue<string>;
}

export class GroupsPageContentView extends VssComponent<IGroupsPageContentViewProps, GroupsPageContentViewState> {
    private _tabProviders: IObservableArray<IVssContributedTab>;
    private _pageStateHistoryService: IPageStateHistoryService;
    private _selectedSubjectDescriptor: ObservableValue<string>;

    private buttonProps: IButtonProps[] = [
        {
            text: Resources.AddAdministrators,
            onClick: this.props.openAddMemberPanel
        }
    ];

    public constructor(props: IGroupsPageContentViewProps) {
        super(props);

        this.state = {
            selectedTab: new ObservableValue(this._getDefaultSelectedTab())
        };

        this._pageStateHistoryService = this.props.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this._selectedSubjectDescriptor = new ObservableValue("");
        this._selectedSubjectDescriptor.subscribe(this.onSelectedSubjectDescriptorChanged);
        this._pageStateHistoryService.registerObservable(this._selectedSubjectDescriptor, "subjectDescriptor", HistoryBehavior.newEntry);
        this.props.initView();
    }

    public render(): JSX.Element {
        this._tabProviders = new ContributedTabProvider({
            pageContext: this.props.context.pageContext,
            tabGroupIds: this._getSelectedTabGroup(),
            updateUrlOnPivotChange: true,
            props: {
                filter: this.props.hubViewState.filter
            }
        });

        let selectedGraphSubject = this.props.selectedGroup ?? this.props.selectedUser ?? this.props.selectedServicePrincipal ?? this.props.selectedMember;
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column" tabProviders={this._tabProviders} selectedTabId={this.state.selectedTab}>
                    {this.props.pcaGroup && this.state.selectedTab.value === Constants.groupsTab && !this._isAddGroupDisabled()
                        ? this._generatePCAWarning()
                        : null}
                    <CustomHeader className="bolt-header-with-commandbar">
                        {selectedGraphSubject && !this.props.updatedAvatarUrl ? (
                            <Persona
                                className="display-members-persona"
                                size={PersonaSize.size40}
                                identity={convertGraphMemberToIIdentity(
                                    this.props.context.pageContext,
                                    selectedGraphSubject,
                                    this.props.selectedGroup !== undefined
                                )}
                            />
                        ) : null}
                        {this.props.updatedAvatarUrl ? (
                            <VssPersona {...this._getPersona()} size="medium-plus" className="display-members-persona" />
                        ) : null}
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                    {this._getSelectedSubjectName()}
                                </HeaderTitle>
                            </HeaderTitleRow>
                            <HeaderDescription>{this._getGroupDescription()}</HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>

                    <TabBar onSelectedTabChanged={this._onSelectedTabChanged} renderAdditionalContent={this._renderAdditionalContent} />
                    <div className="page-content page-content-top">
                        <TabContent />
                    </div>
                </Page>
                {this.props.showAddMemberPanel && this.props.pcaGroup ? (
                    <WrappedComponent<MembershipAddPanelProps>
                        wrappedType="ms.vss-admin-web.orggroups-membership-add-panel"
                        dependencies={["ms.vss-admin-web.admin-dialogs-groups-content"]}
                        wrappedProps={{
                            panelTitle: Resources.AddMemberPanelTitle + this.props.pcaGroup.displayName,
                            onDismiss: this.props.closeAddMemberPanel,
                            pageContext: this.props.context.pageContext,
                            onSave: this._onSaveMembers,
                            description: localeFormat(Resources.AddMemberPanelDescription, Resources.Group.toLocaleLowerCase()),
                            identityTypes: [IEntityType.Group, IEntityType.User, IEntityType.ServicePrincipal],
                            peoplePickerLabel: Resources.AddUsersOrGroups,
                            peoplePickerPlaceholder: Resources.SearchUsersOrGroups,
                            onLogTelemetry: this.props.logTelemetry,
                            checkMembershipExistence: this._checkMembershipExistence,
                            removeMembers: this.props.removeMembers,
                            invalidMembers: this.props.invalidMembers,
                            validMembers: this.props.validMembers,
                            permissions: {}
                        }}
                    />
                ) : null}
            </Surface>
        );
    }

    private _getPersona(): IVssPersonaProps {
        const avatarPersona = {
            suppressPersonaCard: true,
            displayName: this._getSelectedSubjectName(),
            showInitialsOnImageError: true,
            imageUrl: "",
            imgAltText: ""
        };

        if (this.props.updatedAvatarUrl) {
            (avatarPersona.imageUrl = this.props.updatedAvatarUrl), (avatarPersona.imgAltText = Resources.ImageAlt);
        }

        return avatarPersona;
    }

    private _getSelectedMemberDescriptor(): string | undefined {
        const { selectedGroup, selectedUser, selectedServicePrincipal, selectedMember } = this.props;
        return selectedGroup?.descriptor ?? selectedUser?.descriptor ?? selectedServicePrincipal?.descriptor ?? selectedMember?.descriptor ?? "";
    }

    // This will get called on view switch from UI and when browser navigation is used
    private onSelectedSubjectDescriptorChanged = (value: string, action: string): void => {
        const subjectDescriptor = this._getSelectedMemberDescriptor();
        if (subjectDescriptor && subjectDescriptor != value) {
            this.props.initGroupsPivot();
        }
    };

    private _checkMembershipExistence = (member: IIdentity) => {
        if (this.props.pcaGroup) {
            this.props.checkMembershipExistence(member, this.props.pcaGroup.descriptor);
        }
    };

    private _onSaveMembers = (subjectIdentities: IIdentity[]) => {
        this.props.logTelemetry(GroupsComponent.GroupsView, "Add PCA", {});
        if (subjectIdentities.length > 0 && this.props.pcaGroup) {
            let permissionGroup = {
                ...(this.props.pcaGroup as PermissionsGroup),
                isAadGroup: false,
                updateImage: false,
                isDefaultTeam: false
            };

            this.props.onSaveMembers(subjectIdentities, permissionGroup, "");
        }
    };

    public componentDidMount(): void {
        this.props.logTelemetry(GroupsComponent.GroupsView, "Page loaded", {});
    }

    public componentDidUpdate(prevProps: IGroupsPageContentViewProps) {
        const { selectedNavType } = this.props;
        const prevDescriptor = prevProps.selectedGroup?.descriptor 
            ?? prevProps.selectedUser?.descriptor
            ?? prevProps.selectedServicePrincipal?.descriptor
            ?? prevProps.selectedMember?.descriptor
            ?? "";
        const selectedDescriptor = this._getSelectedMemberDescriptor();

        if (prevDescriptor != selectedDescriptor || prevProps.selectedNavType != selectedNavType) {
            this._onSelectedTabChanged(this._getDefaultSelectedTab()); // Get default tab for groups-details and user-details
        }

        if (selectedDescriptor && this._selectedSubjectDescriptor.value != selectedDescriptor) {
            this._selectedSubjectDescriptor.value = selectedDescriptor;
        }

        if (this.props.toMarkPermissionsTTI && !prevProps.toMarkPermissionsTTI && this.props.isURLRedirect) {
            const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationPermissions);
        }
    }

    private _generatePCAWarning(): React.ReactNode {
        return (
            <MessageCard
                severity={MessageCardSeverity.Warning}
                className="margin-top-16 pca-warning"
                onDismiss={this.props.closePCAMessageBar}
                buttonProps={this.buttonProps}
            >
                {Resources.PCAWarning}
            </MessageCard>
        );
    }

    private _getGroupDescription(): string {
        const { selectedGroup } = this.props;
        return selectedGroup ? selectedGroup.description : "";
    }

    private _getSelectedSubjectName(): string {
        const { selectedGroup, selectedUser, selectedServicePrincipal, selectedMember } = this.props;
        return selectedGroup?.principalName ?? selectedUser?.displayName ?? selectedServicePrincipal?.displayName ?? selectedMember?.displayName ?? Resources.PermissionsPageHeader;
    }

    private _isAddGroupDisabled(): boolean {
        return this.props.isAddGroupDisabled;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        this.props.logTelemetry(GroupsComponent.GroupsView, "Selected tab changed", { NewTabId: newTabId });
        this.setState({ selectedTab: new ObservableValue(newTabId) });
    };

    private _getDefaultSelectedTab(): string {
        switch (this.props.selectedNavType) {
            case Constants.GroupsNavType.GroupDetails:
            case Constants.GroupsNavType.UserDetails:
            case Constants.GroupsNavType.ServicePrincipalDetails:
            case Constants.GroupsNavType.MemberDetails:
                return Constants.permissionsTab;

            default:
                return Constants.groupsTab;
        }
    }

    private _getSelectedTabGroup(): string[] {
        switch (this.props.selectedNavType) {
            case Constants.GroupsNavType.GroupDetails:
                return [Constants.GroupsPageDetailsTabGroup];

            case Constants.GroupsNavType.UserDetails:
            case Constants.GroupsNavType.ServicePrincipalDetails:
            case Constants.GroupsNavType.MemberDetails:
                return [Constants.UsersPageDetailsTabGroup];

            default:
                return [Constants.GroupsPageTabGroup];
        }
    }

    private _renderAdditionalContent = (): JSX.Element | undefined => {
        if ([Constants.groupsTab, Constants.usersTab, Constants.servicePrincipalsTab, Constants.membersTab, Constants.groupMembersTab, Constants.memberOfTab].indexOf(this.state.selectedTab.value) !== -1) {
            return (
                <FilterBar filter={this.props.hubViewState.filter}>
                    <KeywordFilterBarItem
                        placeholder={this.props.filterPlaceholder || Resources.FilterUsersAndGroups}
                        filterItemKey={Constants.SearchKeyword}
                    />
                </FilterBar>
            );
        }

        return undefined;
    };
}

// Update component props whenever the store's state changes
function mapStateToProps(state: IGroupsViewState, providedProps: IOrganizationGroupsContainerProps): Partial<IGroupsPageContentViewProps> {
    return {
        ...providedProps,
        selectedGroup: ViewSelectors.getSelectedGroup(state),
        selectedUser: ViewSelectors.getSelectedUser(state),
        selectedServicePrincipal: ViewSelectors.getSelectedServicePrincipal(state),
        selectedMember: ViewSelectors.getSelectedMember(state),
        selectedNavType: ViewSelectors.getSelectedNavType(state),
        updatedAvatarUrl: ViewSelectors.getUpdatedAvatarUrl(state),
        filterPlaceholder: ViewSelectors.setFilterPlaceholder(state),
        toMarkPermissionsTTI: ViewSelectors.getToMarkPermissionsTTI(state),
        isURLRedirect: ViewSelectors.isURLRedirect(state),
        showAddMemberPanel: ViewSelectors.showAddMemberPanel(state),
        invalidMembers: ViewSelectors.getInvalidMembers(state),
        validMembers: ViewSelectors.getValidMembers(state),
        pcaGroup: ViewSelectors.getPcaGroup(state),
        isAddGroupDisabled: GroupSelectors.isAddGroupDisabled(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GroupsViewActions.initView,
    logTelemetry: GroupsViewActions.logTelemetry,

    closeAddGroupPanel: GroupsPivotActions.closeAddGroupPanel,
    initGroupsPivot: GroupsPivotActions.initView,

    openAddMemberPanel: GroupsViewActions.openAddMemberPanel,
    closeAddMemberPanel: GroupsViewActions.closeAddMemberPanel,
    onSaveMembers: GroupsViewActions.onSaveMembers,
    removeMembers: GroupsViewActions.removeMembers,
    checkMembershipExistence: GroupsViewActions.checkMembershipExistence,
    closePCAMessageBar: GroupsViewActions.closePCAMessageBar
};

export const ConnectedGroupsPageContentView = connect(mapStateToProps, ActionsToDispatch)(GroupsPageContentView);
